import { ReactNode } from 'react';

export enum FETCH_METHODS {
  GET = 'GET',
  PATCH = 'PATCH',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

export enum TitleVariant {
  Primary = 'primary',
  Secondary = 'Secondary',
}

export type ProviderProps = {
  children: ReactNode;
};

export type TabType = { label: string; id: string; disabled?: boolean };

export type TagType = { label: string; value: string };

export type ColorFilterType = { color: string; colorCode: string };

export type CharacteristicsType = {
  pattern?: string;
  name: string;
  value: string;
};

export type NewsType = {
  id: string;
  title: LocalesVariants;
  shortText: LocalesVariants;
  body: LocalesVariants;
  image: string;
  categories: CategoryType[];
  views: number;
  adminRating: number;
  showInPopular: boolean;
  showBanner?: boolean;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type LocalesVariants = {
  kz: string;
  ru: string;
  en: string;
};

export type ShopType = {
  id: string;
  name: LocalesVariants;
  images: string[];
  address: LocalesVariants;
  phones: {
    number: string;
  }[];
  workTime: LocalesVariants;
  isGeneral: boolean;
  town: LocalesVariants;
};

export type CountAvailableShops = {
  count: number;
  shop: ShopType;
};

export enum Sizes {
  Big = 'big',
  Medium = 'medium',
  Small = 'small',
}

export enum Variants {
  Outline = 'outline',
  Filled = 'filled',
  Ghost = 'ghost',
}

export enum Palettes {
  Primary = 'primary',
  Black = 'black',
  Gray = 'gray',
  Transparent = 'transparent',
  Custom = 'custom',
}

export enum PageView {
  List = 'list',
  Grid = 'grid',
}

export type CategoryType = {
  id: string;
  name: LocalesVariants;
  parent: string;
  level: number;
  image: string;
  slug: string;
};

export type ProductTagType = {
  text: LocalesVariants;
  tag: 'new';
};

export type DiscountType = {
  id: string;
  percent: number;
  dateFrom: string;
  dateEnd: string;
  title: LocalesVariants;
  tag: LocalesVariants;
  description: LocalesVariants;
  starText: LocalesVariants;
  image: string;
};

export type SpecificationType = {
  type: 'size' | 'description';
  title: LocalesVariants;
  width: string;
  height: string;
  length: string;
  text: LocalesVariants;
};

export type SizeType = {
  length: number;
  height: number;
  width: number;
};

export type AdditionalOptionType = {
  color: LocalesVariants;
  colorCode: string;
  image: string;
  simpleOption: boolean;
  text: LocalesVariants;
  title: LocalesVariants;
  article: string;
  id: string;
  idModel: string;
  variantId: {
    article: string;
    color: string;
    createdAt: string;
    description: LocalesVariants;
    images: string[];
    name: LocalesVariants;
    shortName: string;
    type: string;
    updatedAt: string;
    _id: string;
    id: string;
  };
  type?: string;
  images?: string[];
  name: LocalesVariants;
  parentTitle: LocalesVariants;
  shortName: string;
};

export type BackendTagType = {
  adminRating: number;
  createdAt: string;
  id: string;
  param: number;
  showInBlock: boolean;
  tag: string;
  text: LocalesVariants;
  updatedAt: string;
};

export type ProductTypeExtended = {
  id: string;
  _id: string;
  name: LocalesVariants;
  relatedGoods: ProductTypeExtended[];
  categories: CategoryType[];
  description: LocalesVariants;
  images: string[];
  tags: BackendTagType[];
  price: number;
  slug: string;
  fullSlug: string[];
  production: string;
  oldPrice: number;
  baseOptions: {
    color: string;
    colorCode: string;
    good: string;
    size: {
      height: number;
      length: number;
      width: number;
    };
  }[];
  goodsGroup: {
    id: string;
    name: string;
    goods: ProductTypeExtended[];
    createdAt: string;
    updatedAt: string;
  };
  article: string;
  rating: number;
  reviewsCount: number;
  specifications: SpecificationType[];
  additionalOptions: {
    title: LocalesVariants;
    simpleOption: boolean;
    variants: AdditionalOptionType[];
    type?: string;
  }[];
  color: string;
  size: SizeType;
  enabled: boolean;
  type: string;
  rooms: string[];
  views: number;
  adminRating: number;
  showInPopular: boolean;
  adminRatingNew: number;
  showInNew: boolean;
  adminRatingDiscount: number;
  showInDiscount: boolean;
  adminRatingSuit: number;
  showInSuit: boolean;
  createdAt: string;
  updatedAt: string;
};

export type FeedbackType = {
  name: string;
  date: string;
  rating: number;
  images: string[];
  text: string;
  id: number;
};

export type VacancyType = {
  id: string;
  position: LocalesVariants;
  salaryFrom: LocalesVariants;
  salaryTo: LocalesVariants;
  link: string;
  body: LocalesVariants;
  image: string;
  mobileImage: string;
  tabletImage: string;
};

export type FAQCategoryType = {
  id: string;
  name: LocalesVariants;
};

export type FAQResultsType = {
  results: {
    id: string;
    question: LocalesVariants;
    answer: LocalesVariants;
    category: {
      id: string;
    };
  }[];
};

export type VacancyResultsType = {
  results: VacancyType[];
};

export type BannerContentPosition =
  | 'leftTop'
  | 'leftCenter'
  | 'leftBottom'
  | 'center';

export type MainBannerType = {
  id: string;
  title: LocalesVariants;
  titleColor: string;
  text: LocalesVariants;
  textColor: string;
  starText: LocalesVariants;
  starTextColor: string;
  dateFrom: string;
  dateTo: string;
  mediaFile: string;
  link: string;
  buttonText: LocalesVariants;
  buttonTextColor: string;
  contentPosition?: BannerContentPosition;
  enabled: boolean;
  mobileMediaFile: string;
  tabletMediaFile: string;
  headerButtonColor: string;
};

export type CategoryItemType = {
  id: string;
  level: number;
  name: LocalesVariants;
  views: number;
  adminRating: number;
  showInPopular: boolean;
  image: string;
  children?: CategoryItemType[];
  icon: string;
  count: number;
  slug: string;
  isCatalog: boolean;
};

export type RoomType = {
  id: string;
  name: LocalesVariants;
  images: string[];
  adminRating: number;
  showInBlock: boolean;
  categories: CategoryType[];
  slug: string;
};

export type InteriorGoodType = {
  x: number;
  y: number;
  good: {
    _id: string;
    images: string[];
    name: LocalesVariants;
    article?: string;
    price: number;
    fullSlug: string[];
  };
};

export type InteriorType = {
  id: string;
  name: LocalesVariants;
  image: string;
  goods: InteriorGoodType[];
  adminRating: number;
  showInPopular: boolean;
  category: {
    id: string;
    name: LocalesVariants;
    order: number;
  };
};

export type InstagramReviewType = {
  id: string;
  comment: LocalesVariants;
  link: string;
  images: string[];
};

type DateFields = {
  createdAt: string;
  updatedAt: string;
};

type AdminRatingFields = {
  adminRating: number;
  showInPopular: boolean;
};

type ProductTag = ProductTagType & {
  id: string;
  param: number;
  showInBlock: boolean;
} & DateFields &
  AdminRatingFields;

type CategoryWithMetadata = CategoryType & {
  views: number;
} & DateFields &
  AdminRatingFields;

type RoomWithCategories = RoomType & {
  categories: CategoryWithMetadata[];
  link: string;
} & DateFields;

type ProductResultType = {
  id: string;
  name: LocalesVariants;
  categories: CategoryWithMetadata[];
  description: LocalesVariants;
  smallDescription: LocalesVariants;
  images: string[];
  style: string;
  tags: ProductTag[];
  slug: string;
  fullSlug: string[];
  discount: DiscountType & {
    bannerRating: number;
    showInBanner: boolean;
  } & DateFields;
  article: string;
  rating: number;
  reviewsCount: number;
  specifications: SpecificationType[];
  color: string;
  material: string;
  size: {
    height: number;
    length: number;
    width: number;
  };
  enabled: boolean;
  type: 'simple';
  rooms: RoomWithCategories[];
  price: number;
  views: number;
  id1C: string;
  adminRatingNew: number;
  showInNew: boolean;
  adminRatingDiscount: number;
  showInDiscount: boolean;
  adminRatingSuit: number;
  showInSuit: boolean;
  ndsPercent: number;
  ndsIsInSum: boolean;
  haveAssembling: boolean;
  basicUnitCode: string;
  bitrixId: string;
  production: string;
  oldPrice?: number;
} & DateFields &
  AdminRatingFields;

export type CategoryResultsType = {
  page: number;
  limit: number;
  resultCount: number;
  results: ProductResultType[];
};

export type FilterResultsType = {
  style: [];
  colors: ColorFilterType[];
  material: string[];
  enabled: boolean[];
  type: string[];
  haveAssembling: boolean[];
  basicUnitCode: string[];
  production: string[];
  baseOptions: {
    color: string[];
    colorCode: string[];
    size: {
      length: { min: number; max: number };
      height: { min: number; max: number };
      width: { min: number; max: number };
    };
    material: string[];
  };
  additionalOptions: {
    title: string;
    variants: {
      id: string;
      name: string;
      color: string;
      idModel: string;
    }[];
  }[];
  specifications: {
    title: string;
    text: string[];
  }[];
  discount: [];
  rooms: [];
  size: {
    length: { min: number; max: number };
    width: { min: number; max: number };
    height: { min: number; max: number };
  };
  price: {
    min: number;
    max: number;
  };
  resultCount: number;
};

export enum SORTS {
  Popular = 'popular',
  Discount = 'discount',
  Suit = 'suit',
  Cheap = 'cheap',
  Expensive = 'expensive',
  New = 'new',
  Search = 'search',
}

export type DescriptionBlockType = {
  id: string;
  title: LocalesVariants;
  body: LocalesVariants;
  page: string;
  mediaFile: string;
  enabled: boolean;
};

export type DiscountsResultsType = {
  results: DiscountType[];
};

export type CertificateType = {
  id: string;
  name: LocalesVariants;
  file: string;
  order: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ContactType = {
  id: string;
  type: 'phone';
  number: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  icon: string;
};

export type UserType = {
  id: string;
  name: string;
  surname: string;
  gender: string;
  dob: string;
  language: string;
  email: string;
  phone: string;
  role: string;
  address: AddressType;
  savedAddresses: AddressType[];
  emailConfirmed: boolean;
  bitrixId: string;
  createdAt: string;
  updatedAt: string;
};

export type CartGoodType = {
  id: string;
  good: ProductResultType;
  count: number;
  assemblingsCount: number;
  additionalOptions: AdditionalOptionType[];
};

export type AddressType = {
  fullAddress: string;
  entrance: string;
  floor: string;
  flat: string;
};

export type AdditionalService = {
  cost: number;
  count: number;
  productCount: number;
  rowId: string;
  id: string;
  good: ProductResultType;
};

export type CartDataType = {
  id: string;
  name: string;
  number: string;
  goods: CartGoodType[];
  assemblingCost: number;
  additionalService: AdditionalService[];
  email: string;
  phone: string;
  type: 'pickup' | 'delivery';
  address: AddressType;
  transactions: {
    id: string;
    user: UserType;
    order: string;
    sum: number;
    write_off: boolean;
    status: 'created';
    type: 'bonus';
    paymentType: 'card';
    bill: string;
    data: Record<string, unknown>;
    createdAt: string;
    updatedAt: string;
  }[];
  comment: string;
  user: UserType;
  status: 'cart';
  isFreightElevator: boolean;
  defaultCost: number;
  deliveryCost: number;
  additionalOptionsCost: number;
  discount: number;
  promocode: {
    id: string;
    code: string;
    discountInPercent: number;
    createdAt: string;
    updatedAt: string;
  };
  promocodeDiscount: number;
  bonusesToAdd: number;
  finalCost: number;
  returnedSum: number;
  bonusesToWriteOff: number;
  paymentMethod: {
    id: string;
    card: string;
    user: UserType;
    type: 'card';
    companyName: string;
    bin: string;
    bank: string;
    legalAdress: string;
    iik: string;
    bik: string;
    createdAt: string;
    updatedAt: string;
  };
  paymentMethodType: string;
  paymentDate: string;
  paymentType: 'card' | 'googlePay' | 'applePay' | 'ePay' | '004';
  payerType: 'ur' | 'phiz';
  orderFormedDate: string;
  ndsSum: number;
  ndsIsInSum: boolean;
  id1C: string;
  status1C: string;
  bitrixId: string;
  createdAt: string;
  updatedAt: string;
};

export type PaymentMethodType = {
  bank: string;
  bik: string;
  bin: string;
  card: string;
  companyName: string;
  createdAt: string;
  id: string;
  iik: string;
  legalAdress: string;
  type: string;
  updatedAt: string;
  user: string;
};

export type OrderType = {
  comment: string;
  createdAt: string;
  defaultCost: number;
  deliveryCost: number;
  discount: number;
  finalCost: number;
  goods: CartGoodType[];
  id: string;
  isFreightElevator: boolean;
  number: string;
  payerType: string;
  paymentDate: string;
  returnedSum: number;
  status: OrderStatus;
  transactions: [];
  type: string;
  updatedAt: string;
};

export type OrderExtendedType = {
  additionalOptionsCost: number;
  address: AddressType;
  bonusesToAdd: number;
  assemblingCost: number;
  bonusesToWriteOff: number;
  comment: string;
  createdAt: string;
  defaultCost: number;
  deliveryCost: number;
  discount: number;
  email: string;
  finalCost: number;
  goods: CartGoodType[];
  id: string;
  isFreightElevator: boolean;
  name: string;
  ndsIsInSum: boolean;
  ndsSum: number;
  number: string;
  orderFormedDate: string;
  payerType: string;
  phone: string;
  promocodeDiscount: number;
  returnedSum: number;
  status: OrderStatus;
  transactions: [];
  type: string;
  updatedAt: string;
  user: UserType;
  paymentType: OrderPayment;
};

export enum OrderPayment {
  ePay = 'ePay',
  Tarlan = 'card',
  GooglePay = 'googlePay',
  ApplePay = 'applePay',
  Installment = '004',
}

export enum OrderStatus {
  Cart = 'cart',
  Formed = 'formed',
  Pending = 'pending',
  Paid = 'paid',
  Delivered = 'delivered',
  Canceled = 'canceled',
  Returned = 'returned',
  PartReturned = 'partReturned',
  ReturnedToCart = 'returnedToCart',
}

export type TransactionType = {
  access_token: string;
  expires_in: string;
  refresh_token: string;
  scope: string;
  token_type: string;
};

export type CompanyInvoiceData = {
  companyName?: string;
  BIN?: string;
  bank?: string;
  urAddress?: string;
  IIK?: string;
  BIK?: string;
};

export type ReviewType = {
  id: string;
  good: null;
  user: {
    isCompany: boolean;
    _id: string;
    name: string;
    surname: string;
    gender: string;
    dob: string;
    language: string;
    email: string;
    phone: string;
    role: string;
    saved_addresses: string[];
    emailConfirmed: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
    savedAddresses: string[];
  };
  goodRating: number;
  serviceRating: number;
  averageRating: number;
  images: string[];
  text: string;
  adminRating: number;
  showInBlock: boolean;
  createdAt: string;
  updatedAt: string;
};

export type DadataSuggestionType = {
  value: string;
  data: {
    postal_code: string;
    country: string;
    region: string;
    city: string;
    street: string;
    house: string;
  };
};

export type DadataPartySuggestionType = {
  value: string;
  data: {
    inn?: string;
    kpp?: string;
    ogrn?: string;
    name: {
      full_with_opf?: string;
      short_with_opf?: string;
      full?: string;
      short?: string;
    };
    address?: {
      value?: string;
    };
  };
};

export type TempCartItemType = {
  good: string;
  info: {
    name: LocalesVariants;
    price: number;
    oldPrice?: number;
    images: string[];
    specifications: SpecificationType[];
    finalPrice: number;
  };
  count: number;
  additionalOptions?: AdditionalOptionType[];
};

export type CityType = {
  city: string;
  link?: string;
};

export type PartnerAdvantageType = {
  id: string;
  text: LocalesVariants;
  order: number;
  showInSlider: boolean;
  createdAt: string;
  updatedAt: string;
};

export type DistributorType = {
  id: string;
  name: LocalesVariants;
  country: LocalesVariants;
  town: LocalesVariants;
  title: LocalesVariants;
  address: LocalesVariants;
  phones: {
    type: 'phone';
    number: string;
    icon: string;
  }[];
  email: string;
  order: number;
  showInSlider: boolean;
  createdAt: string;
  updatedAt: string;
};

export type PartnerType = {
  id: string;
  name: LocalesVariants;
  link: string;
  logo: string;
  order: number;
  showInSlider: boolean;
  createdAt: string;
  updatedAt: string;
};

export type SeoType = {
  id: string;
  title: LocalesVariants | string;
  text: LocalesVariants | string;
  page: string;
  image: string;
  canonical: string;
  robots: string;
  keywords: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type OftenSearchType = {
  id: string;
  name: string;
};

export type BonusesDataType = {
  bonusAmount: number;
  bonusPercent: number;
  pkpassUrl: string;
};

export type CompanyValueType = {
  id: string;
  title: LocalesVariants;
  text: LocalesVariants;
  mediaFile: string;
  order: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type StaticPageListItem = {
  id: string;
  link: string;
  title: LocalesVariants;
  description: LocalesVariants;
  type: string;
  page: string;
  enabled: boolean;
  icon: string;
  createdAt: string;
  updatedAt: string;
  text: LocalesVariants;
};
